import { useEffect, useRef } from "react";

export default function useMobileCartDrag({ expanded, hasItems, onExpandedChange }) {
  const panelRef = useRef(null);
  const gesture = useRef(null);
  const suppressClick = useRef(false);

  const reset = () => {
    panelRef.current?.style.removeProperty("--pos-cart-drag-y");
    panelRef.current?.classList.remove("is-dragging");
    gesture.current = null;
  };

  useEffect(() => {
    const cancel = () => {
      panelRef.current?.style.removeProperty("--pos-cart-drag-y");
      panelRef.current?.classList.remove("is-dragging");
      gesture.current = null;
    };
    window.addEventListener("resize", cancel);
    return () => {
      window.removeEventListener("resize", cancel);
      cancel();
    };
  }, []);

  const onPointerDown = (event) => {
    if (!window.matchMedia("(max-width: 991.98px)").matches || (!hasItems && !expanded)) return;
    if (event.isPrimary === false || event.button > 0 || gesture.current) return;
    const panel = panelRef.current;
    if (!panel) return;
    suppressClick.current = false;
    const peekRem = parseFloat(getComputedStyle(panel).getPropertyValue("--pos-cart-peek-height")) || 3.55;
    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    gesture.current = {
      id: event.pointerId,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      velocity: 0,
      delta: 0,
      expanded,
      travel: Math.max(0, panel.getBoundingClientRect().height - peekRem * rootSize),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId) return;
    const delta = event.clientY - current.startY;
    const elapsed = event.timeStamp - current.lastTime;
    if (elapsed > 0) current.velocity = (event.clientY - current.lastY) / elapsed;
    current.lastY = event.clientY;
    current.lastTime = event.timeStamp;
    current.delta = delta;
    if (Math.abs(delta) > 6) suppressClick.current = true;
    if (!suppressClick.current) return;
    const min = current.expanded ? 0 : -current.travel;
    const max = current.expanded ? current.travel : 0;
    const offset = delta < min ? min + (delta - min) * 0.12 : delta > max ? max + (delta - max) * 0.12 : delta;
    panelRef.current?.classList.add("is-dragging");
    panelRef.current?.style.setProperty("--pos-cart-drag-y", `${offset}px`);
  };

  const finish = (event, cancelled = false) => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId) return;
    if (!cancelled && suppressClick.current) {
      const fast = event.timeStamp - current.lastTime < 100 && Math.abs(current.velocity) > 0.45;
      const far = Math.abs(current.delta) > Math.min(72, current.travel * 0.22);
      const direction = fast ? current.velocity : current.delta;
      onExpandedChange(fast || far ? direction < 0 : current.expanded);
    }
    reset();
  };

  const consumeDragClick = () => {
    const dragged = suppressClick.current;
    suppressClick.current = false;
    return dragged;
  };

  return {
    panelRef,
    consumeDragClick,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (event) => finish(event),
      onPointerCancel: (event) => finish(event, true),
      onLostPointerCapture: (event) => finish(event, true),
    },
  };
}
